// Central SEO config — business NAP, JSON-LD builders, per-category
// meta and keyword landing-page content. Domain is the final
// production domain confirmed by the operator.

export const SITE = {
  url: 'https://eg3promotions.com',
  name: 'Evergreen Graphics Group Inc',
  shortName: 'Evergreen Graphics Group',
  phone: '(905) 447-0257',
  email: 'info@eg3.ca',
  address: {
    street: '90 Venture Dr #2',
    city: 'Scarborough',
    region: 'ON',
    postal: 'M1B 3L6',
    country: 'CA',
  },
};

export const localBusinessJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE.url}/#business`,
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/evergreen-logo.png`,
  image: `${SITE.url}/evergreen-logo.png`,
  telephone: SITE.phone,
  email: SITE.email,
  priceRange: '$$',
  description:
    'Custom die-cut stickers, roll labels, vinyl decals, DTF heat transfers, patches and specialty print products. Instant online quoting with AI mockups — printed in Canada, shipped across Canada & the USA.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postal,
    addressCountry: SITE.address.country,
  },
  areaServed: [
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'United States' },
  ],
});

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: `${SITE.url}${item.path}`,
  })),
});

export const productJsonLd = ({ name, description, path, image }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  url: `${SITE.url}${path}`,
  image: image || `${SITE.url}/evergreen-logo.png`,
  brand: { '@type': 'Brand', name: SITE.shortName },
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'CAD',
    availability: 'https://schema.org/InStock',
    url: `${SITE.url}/quote`,
    lowPrice: '9.99',
    highPrice: '999.99',
  },
});

export const faqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const blogPostJsonLd = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.metaDescription,
  datePublished: post.date,
  dateModified: post.date,
  url: `${SITE.url}/blog/${post.slug}`,
  mainEntityOfPage: `${SITE.url}/blog/${post.slug}`,
  image: `${SITE.url}/evergreen-logo.png`,
  author: { '@type': 'Organization', name: SITE.name, url: SITE.url },
  publisher: {
    '@type': 'Organization',
    name: SITE.name,
    logo: { '@type': 'ImageObject', url: `${SITE.url}/evergreen-logo.png` },
  },
});

export const CATEGORY_SEO = {
  stickers: {
    title: 'Custom Stickers — Die-Cut, Holographic, Glitter & More | Evergreen Graphics Group',
    description:
      'Order custom stickers online in any size, shape & quantity. Die-cut singles, transparent, holographic, glitter & permanent stickers. Instant quotes — Canada & USA shipping.',
  },
  labels: {
    title: 'Custom Roll Labels — BOPP, Foil & Vinyl Product Labels | Evergreen Graphics Group',
    description:
      'Custom roll labels for jars, bottles & packaging. Clear BOPP, white BOPP, gold & silver foil, writable and semigloss paper. Instant online quotes — ships Canada & USA.',
  },
  'decals-clings': {
    title: 'Custom Decals & Static Clings — Wall, Floor & Window | Evergreen Graphics Group',
    description:
      'Custom vinyl decals, wall graphics, floor & street decals, clear decals and static window clings. Any size & shape, printed in Canada, shipped across Canada & the USA.',
  },
  wearables: {
    title: 'DTF Heat Transfers, Patches & Name Badges | Evergreen Graphics Group',
    description:
      'Custom DTF heat transfers, iron-on emblems & patches, name badges and temporary tattoos. Pro-quality wearable printing with instant online quotes — Canada & USA.',
  },
  'unique-custom': {
    title: 'Custom Magnets, UV DTF Stickers & Packing Tape | Evergreen Graphics Group',
    description:
      'Unique custom products: fridge & vehicle magnets, UV DTF stickers, multi-ply labels, glow-in-the-dark prints and branded packing tape. Instant quotes — Canada & USA.',
  },
  'specialty-niche': {
    title: 'Specialty Printing — Embossing, Foil & Scratch \'N Sniff | Evergreen Graphics Group',
    description:
      'Specialty print effects: embossing, hot stamping, silkscreen, scratch \'n sniff, colour-changing ink and back-print. Premium finishes with instant online quotes.',
  },
};

export const LANDING_PAGES = [
  {
    slug: 'custom-die-cut-stickers',
    metaTitle: 'Custom Die-Cut Stickers — Any Shape, Any Size | Canada & USA',
    metaDescription:
      'Order custom die-cut stickers online. Any shape, any size, any quantity — waterproof vinyl, instant quotes, free AI mockups. Printed in Canada, fast shipping to Canada & the USA.',
    h1: 'Custom Die-Cut Stickers, Cut to Any Shape',
    intro:
      'Die-cut stickers are cut precisely to the outline of your design — no square backgrounds, no wasted borders. Whether you need 50 logo stickers for a farmers-market stall in Toronto or 10,000 product stickers for a US-wide launch, Evergreen Graphics Group prints premium waterproof vinyl stickers and ships them fast across Canada and the United States.',
    bullets: [
      'Any custom shape — we generate the cutline for you automatically',
      'Waterproof, scratch-resistant & UV-safe vinyl',
      'No minimums that hurt — order from 50 pieces',
      'Perfect as laptop stickers, water bottle stickers, packaging seals & merch',
      'Instant online pricing, live artwork preview & AI lifestyle mockups',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'Why brands choose die-cut stickers',
        body: 'A die-cut sticker follows the exact contour of your artwork, which makes your logo pop on laptops, water bottles, packaging and storefronts. They are the most popular promotional sticker format in North America because every sticker doubles as a mini billboard for your brand. Our thick premium vinyl with laminate finish survives dishwashers, rain, and years of sunlight.',
      },
      {
        heading: 'From upload to doorstep in days',
        body: 'Upload your artwork, and our quoting engine prices your exact size and quantity instantly — no waiting for an email quote. Our built-in preview shows your cutline live, and our AI mockup generator shows your sticker on real-world products before you pay. Orders are produced in our Scarborough, Ontario facility and shipped with live-rate couriers across Canada and the USA.',
      },
    ],
    faqs: [
      {
        q: 'What is the difference between die-cut and kiss-cut stickers?',
        a: 'Die-cut stickers are cut through both the vinyl and the backing paper to match your design\'s shape. Kiss-cut stickers are cut only through the vinyl, leaving a square backing. Die-cut looks more premium in hand; kiss-cut is easier to peel for giveaways.',
      },
      {
        q: 'Are your die-cut stickers waterproof?',
        a: 'Yes. We print on durable vinyl with a protective laminate that is waterproof, dishwasher-safe and UV-resistant — perfect for outdoor use, water bottles and vehicles.',
      },
      {
        q: 'Do you ship custom stickers to the USA?',
        a: 'Yes — we ship across Canada and the United States with live courier rates calculated at checkout, so you always see real shipping costs before you order.',
      },
      {
        q: 'What file format should I upload?',
        a: 'PNG, JPG, PDF, AI or SVG all work. High-resolution PNG with a transparent background gives the best die-cut results, and our system generates the cutline automatically.',
      },
      {
        q: 'Do die-cut stickers work as laptop and water bottle stickers?',
        a: 'Absolutely — die-cut vinyl with laminate is the go-to material for laptop stickers and water bottle stickers. It survives dishwashers, water bottles\' daily washes and years of handling without peeling or fading.',
      },
    ],
    categoryPath: '/category/stickers/product/die-cut-singles',
    categoryLabel: 'Browse Die-Cut Stickers',
  },
  {
    slug: 'custom-roll-labels',
    metaTitle: 'Custom Roll Labels — Product & Packaging Labels | Canada & USA',
    metaDescription:
      'Custom roll labels for bottles, jars, candles & packaging. Clear BOPP, white BOPP, gold & silver foil. Instant online quotes, printed in Canada, shipped across Canada & the USA.',
    h1: 'Custom Roll Labels for Products That Sell',
    intro:
      'From candle jars in Vancouver to sauce bottles in New York, professional roll labels are the fastest way to make a product look retail-ready. Evergreen Graphics Group prints custom roll labels on premium BOPP, foil and paper stocks — with instant online pricing and fast shipping across Canada and the USA.',
    bullets: [
      'Clear BOPP, white BOPP, gold & silver foil, semigloss & writable stocks',
      'Oil-, water- and fridge-resistant materials for food & beverage',
      'Rolls ready for hand application or automatic label applicators',
      'Live pricing on every size and quantity — no quote request emails',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'The right material for your product',
        body: 'Clear BOPP gives a "no-label" premium look on glass and metal. White BOPP is the workhorse for food, beverage and cosmetics — waterproof and squeeze-proof. Gold and silver foil add a metallic shelf-pop for premium lines, and writable BOPP is perfect for batch dates and small-batch makers. Not sure? Our team will recommend the best fit for your packaging.',
      },
      {
        heading: 'Built for small batches and big runs',
        body: 'Order 250 labels for your first market weekend or 50,000 for a distributor order — our quoting engine prices every quantity break instantly so you can find your sweet spot. Every order includes a live artwork preview so what you approve is exactly what lands on your roll.',
      },
    ],
    faqs: [
      {
        q: 'What size roll labels can you print?',
        a: 'We print virtually any custom size — common sizes include 2"x2", 3"x3", 2"x4" and full wrap labels. Enter your exact dimensions in the quote calculator for live pricing.',
      },
      {
        q: 'Are your labels waterproof and oil-resistant?',
        a: 'BOPP materials (clear, white, writable) are waterproof, oil-resistant and fridge/freezer safe — ideal for food, beverage, candles and cosmetics.',
      },
      {
        q: 'Can I use your roll labels with a label applicator machine?',
        a: 'Yes — labels are supplied on rolls with consistent spacing and your choice of unwind direction, compatible with standard automatic and semi-automatic applicators.',
      },
      {
        q: 'Do you offer foil labels?',
        a: 'Yes, we print on gold and silver foil stocks, plus specialty finishes like embossing and hot stamping for premium product lines.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Roll Labels',
  },
  {
    slug: 'custom-vinyl-decals',
    metaTitle: 'Custom Vinyl Decals — Wall, Window, Floor & Vehicle | Canada & USA',
    metaDescription:
      'Custom vinyl decals & graphics for walls, windows, floors and vehicles. Any size & shape, indoor & outdoor rated. Instant quotes — printed in Canada, shipped to Canada & USA.',
    h1: 'Custom Vinyl Decals & Graphics That Turn Heads',
    intro:
      'Transform storefronts, offices, gyms and vehicles with custom-printed vinyl decals. Evergreen Graphics Group produces wall decals, window graphics, floor and street decals, clear decals and static clings — engineered for the surface they live on, and shipped anywhere in Canada and the USA.',
    bullets: [
      'Indoor & outdoor rated vinyl with anti-slip laminate options for floors',
      'Removable or permanent adhesives — no surface damage on removal',
      'Static clings for glass: reposition as often as you like',
      'Any size, from a 3-inch logo decal to full wall murals',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'A decal for every surface',
        body: 'Walls, windows, floors, sidewalks, vehicles — each surface needs the right material. Our wall decals use removable adhesive that won\'t peel paint. Floor and street decals add certified anti-slip laminates. Clear decals disappear into glass leaving just your design, and static clings need no adhesive at all. Tell us where it\'s going and we\'ll match the material.',
      },
      {
        heading: 'Retail, events and fleet branding',
        body: 'Retailers use our window graphics for seasonal promotions, gyms and studios brand their floors and mirrors, and small fleets add vehicle-grade vinyl graphics that survive Canadian winters and US summers alike. Every order gets instant pricing and a live preview before production.',
      },
    ],
    faqs: [
      {
        q: 'Will wall decals damage my paint?',
        a: 'No — our wall decals use a removable adhesive designed to come off cleanly without peeling paint or leaving residue, even after years on the wall.',
      },
      {
        q: 'Are your floor decals slip-rated?',
        a: 'Yes, floor and street decals are finished with a textured anti-slip laminate suitable for high-traffic retail and outdoor walkways.',
      },
      {
        q: 'How long do outdoor vinyl decals last?',
        a: 'Outdoor-rated vinyl with UV laminate typically lasts 3–5 years outdoors, resisting rain, sun and temperature swings across North America.',
      },
      {
        q: 'What is the difference between a decal and a static cling?',
        a: 'Decals use adhesive and are semi-permanent. Static clings grip glass with static only — no glue — so they can be moved and reused, ideal for seasonal window promos.',
      },
    ],
    categoryPath: '/category/decals-clings',
    categoryLabel: 'Browse Decals & Clings',
  },
  {
    slug: 'custom-dtf-heat-transfers',
    metaTitle: 'Custom DTF Heat Transfers — Iron-On for Apparel | Canada & USA',
    metaDescription:
      'Custom DTF (direct-to-film) heat transfers for t-shirts, hoodies & hats. Vibrant, stretchable, wash-resistant. Gang sheets available. Instant quotes — ships Canada & USA.',
    h1: 'Custom DTF Heat Transfers for Pro-Quality Apparel',
    intro:
      'DTF (direct-to-film) transfers are the modern standard for custom apparel — vivid full-colour prints that press onto cotton, polyester and blends, stretch without cracking, and survive 50+ washes. Evergreen Graphics Group supplies print shops, clothing brands, teams and event merch across Canada and the USA.',
    bullets: [
      'Works on cotton, polyester, blends, denim & canvas — light or dark garments',
      'Stretchable, crack-resistant and 50+ wash durable',
      'By-piece transfers or cost-saving gang sheets (by the sheet)',
      'No weeding, no minimum colour counts — photographic detail welcome',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'Why DTF beat vinyl and screen printing for short runs',
        body: 'Traditional screen printing needs setup fees per colour, and HTV vinyl requires tedious weeding. DTF prints your full-colour design in one pass — gradients, photos and fine text included — and presses in 15 seconds with a standard heat press. For runs from 1 to 1,000 pieces, it\'s faster, cheaper and more vibrant.',
      },
      {
        heading: 'Gang sheets that stretch your budget',
        body: 'Our by-the-sheet option lets you tile as many designs as fit onto one large film sheet — mix sizes, mix designs, one low price. Print shops and Etsy sellers use gang sheets to cut per-design costs dramatically while keeping inventory flexible.',
      },
    ],
    faqs: [
      {
        q: 'What temperature and time do I press DTF transfers?',
        a: 'Typically 300–325°F (150–160°C) for 10–15 seconds with medium-firm pressure, then a short cold peel. Full pressing instructions ship with every order.',
      },
      {
        q: 'Do DTF transfers work on dark garments?',
        a: 'Yes — every DTF transfer includes an opaque white underbase, so colours stay vivid on black and dark fabrics without any extra steps.',
      },
      {
        q: 'How many washes do DTF prints last?',
        a: 'Properly pressed DTF transfers withstand 50+ washes without cracking or significant fading. Wash inside-out in cold water for maximum life.',
      },
      {
        q: 'Can I order a gang sheet with multiple designs?',
        a: 'Yes — our DTF by-the-sheet product lets you fill a sheet with any mix of designs and sizes, which is the most cost-effective way to order transfers.',
      },
    ],
    categoryPath: '/category/wearables/product/dtf-heat-transfers',
    categoryLabel: 'Browse DTF Heat Transfers',
  },
  {
    slug: 'custom-holographic-stickers',
    metaTitle: 'Custom Holographic Stickers — Rainbow Chrome Effect | Canada & USA',
    metaDescription:
      'Custom holographic stickers with a rainbow chrome shine. Die-cut to any shape, waterproof & scratch-resistant. Instant online quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Holographic Stickers That Demand Attention',
    intro:
      'Holographic stickers shift through a rainbow chrome spectrum as they catch the light — the single most eye-catching sticker material you can print on. Artists, streamers, breweries and product brands across Canada and the USA use them to make packaging, merch tables and laptop lids impossible to ignore.',
    bullets: [
      'Genuine holographic vinyl with rainbow light-shift effect',
      'Die-cut to any custom shape with automatic cutline generation',
      'Waterproof laminate — dishwasher, rain and scratch resistant',
      'Small-batch friendly: order from 50 pieces with instant pricing',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'How holographic printing works',
        body: 'We print your design onto metallized holographic vinyl. Anywhere you leave white or light colours, the rainbow effect shines through; solid dark inks stay opaque. Smart designs use negative space and light tints to maximize the prism effect — and our live preview shows exactly how your artwork will interact with the material before you order.',
      },
      {
        heading: 'Perfect for merch, packaging and collectibles',
        body: 'Holographic stickers command premium perceived value: artists sell them as standalone merch, brands use them as tamper-evident premium seals, and creators include them as freebies that customers actually keep. Pair them with our glitter and transparent stickers to build a full collectible set.',
      },
    ],
    faqs: [
      {
        q: 'Will my whole sticker look rainbow?',
        a: 'The holographic effect shows through white and lightly-tinted areas of your design. Solid dark colours print opaque. Our live preview shows you the exact result before ordering.',
      },
      {
        q: 'Are holographic stickers waterproof?',
        a: 'Yes — they are laminated with a protective layer making them waterproof, scratch-resistant and dishwasher safe.',
      },
      {
        q: 'What is the minimum order for holographic stickers?',
        a: 'You can order from 50 pieces, with instant quantity-break pricing shown live in our quote calculator.',
      },
      {
        q: 'Can holographic stickers be die-cut to a custom shape?',
        a: 'Absolutely — upload your artwork and our system generates a precise die-cut line around your design automatically.',
      },
      {
        q: 'Are holographic stickers good for laptops and water bottles?',
        a: 'Yes — the laminated holographic vinyl is scratch-resistant and waterproof, making it a favourite for laptop stickers and water bottle stickers that need to survive daily handling while catching the light.',
      },
    ],
    categoryPath: '/category/stickers/product/holographic-stickers',
    categoryLabel: 'Browse Holographic Stickers',
  },
  {
    slug: 'custom-kiss-cut-stickers',
    metaTitle: 'Custom Kiss-Cut Stickers & Sticker Sheets | Canada & USA',
    metaDescription:
      'Order kiss-cut stickers and multi-design sticker sheets online. Easy-peel backing, any shape & size, waterproof vinyl. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Kiss-Cut Stickers & Sticker Sheets',
    intro:
      'Kiss-cut stickers are cut through the vinyl but not the backing paper, leaving a border that makes them effortless to peel — the format of choice for giveaways, planner sticker sheets and multi-design packs. Evergreen Graphics Group prints kiss-cut singles and full sticker sheets with as many designs as you can fit, shipped across Canada and the USA.',
    bullets: [
      'Easy-peel backing — perfect for handing out at events and markets',
      'Sticker sheets: combine multiple designs on one sheet for one low price',
      'Waterproof, scratch-resistant laminated vinyl',
      'Planner, journal and branding sheet layouts welcome',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Why choose kiss-cut over die-cut',
        body: 'Because the backing stays intact, kiss-cut stickers peel faster and protect intricate designs — thin borders, fine lettering and delicate shapes stay flat and safe until applied. The extra backing also gives you room for branding, application instructions or a QR code around each sticker, turning every giveaway into a mini business card.',
      },
      {
        heading: 'Sticker sheets stretch your budget',
        body: 'A sticker sheet gangs multiple kiss-cut designs onto one printed sheet — mix logos, icons, slogans and seasonal art in a single order. Etsy sellers, planners-and-journals brands and marketing teams use sheets to cut per-design costs dramatically while giving customers a full collectible set in one piece.',
      },
    ],
    faqs: [
      {
        q: 'What is a kiss-cut sticker?',
        a: 'A kiss-cut sticker is cut through the vinyl layer only, leaving the backing paper intact. The sticker peels away from a square or rectangular backing, which makes peeling easier and protects detailed designs.',
      },
      {
        q: 'Can I put multiple designs on one sticker sheet?',
        a: 'Yes — sticker sheets are designed for exactly that. Fit as many kiss-cut designs as your sheet size allows; each design peels individually.',
      },
      {
        q: 'Are kiss-cut stickers waterproof?',
        a: 'Yes. The vinyl and laminate are identical to our die-cut stickers — waterproof, scratch-resistant and UV-safe. Only the cutting style differs.',
      },
      {
        q: 'Which is better for giveaways: kiss-cut or die-cut?',
        a: 'Kiss-cut. The intact backing makes stickers faster to peel and gives you printable space around the design for your logo or website, so every giveaway doubles as a business card.',
      },
    ],
    categoryPath: '/category/stickers',
    categoryLabel: 'Browse All Stickers',
  },
  {
    slug: 'custom-bumper-stickers',
    metaTitle: 'Custom Bumper Stickers — Weatherproof Vinyl | Canada & USA',
    metaDescription:
      'Custom bumper stickers printed on outdoor-grade weatherproof vinyl. Any size, slogan or logo — survives sun, rain and car washes. Instant quotes, ships Canada & USA.',
    h1: 'Custom Bumper Stickers Built for the Road',
    intro:
      'From campaign slogans to band logos to small-business advertising, bumper stickers are still one of the highest-visibility promotional products per dollar — a moving billboard that works every day the car does. Evergreen Graphics Group prints bumper stickers on outdoor-grade vinyl that shrugs off Canadian winters, US summers, road salt and car washes.',
    bullets: [
      'Outdoor-rated vinyl with UV laminate — 3-5 year weatherproof life',
      'Classic sizes (11.5"x3", 6"x4") or any custom dimension',
      'Removable adhesive option — comes off clean, no residue',
      'Fade-resistant inks that survive sun, rain and car washes',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Built for bumpers, made for attention',
        body: 'A bumper sticker lives in the harshest environment a print product can face: direct sun, freezing rain, gravel spray and high-pressure washes. We print on thick outdoor vinyl with a UV-protective laminate so colours stay sharp for years, and the adhesive grips paint and plastic bumpers without lifting at the corners.',
      },
      {
        heading: 'Fleets, fundraisers and fan merch',
        body: 'Small fleets add contact info and booking URLs to every vehicle. Nonprofits and school fundraisers sell bumper stickers at margins over 80%. Bands and creators turn fans into rolling promoters. Whatever the message, our instant quote calculator prices your exact size and quantity in seconds.',
      },
    ],
    faqs: [
      {
        q: 'How long do bumper stickers last outdoors?',
        a: 'Our outdoor vinyl with UV laminate typically lasts 3–5 years on a vehicle, resisting fading, rain, snow and regular car washes.',
      },
      {
        q: 'Will a bumper sticker damage my car\'s paint?',
        a: 'No — choose the removable adhesive option and the sticker comes off clean without residue or paint damage, even after years on the bumper.',
      },
      {
        q: 'What is the standard bumper sticker size?',
        a: 'The classic size is 11.5" x 3", with 6" x 4" popular for smaller messages. We print any custom size — enter your dimensions in the quote calculator for live pricing.',
      },
      {
        q: 'Can I order bumper stickers in small quantities?',
        a: 'Yes — order from 50 pieces with instant quantity-break pricing, so fundraisers and small campaigns don\'t need to over-order.',
      },
    ],
    categoryPath: '/category/stickers/product/permanent-stickers',
    categoryLabel: 'Browse Permanent Stickers',
  },
  {
    slug: 'custom-candle-labels',
    metaTitle: 'Custom Candle Labels & Product Labels for Makers | Canada & USA',
    metaDescription:
      'Custom candle labels, jar labels & product labels for small-batch makers. Heat-resistant, oil-proof BOPP & foil stocks. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Candle Labels & Product Labels That Sell',
    intro:
      'Your product is handmade — your label shouldn\'t look it. From soy candles and bath products to sauces, soaps and cosmetics, Evergreen Graphics Group prints retail-ready product labels on heat-resistant, oil-proof materials that make small-batch products look big-brand on the shelf, at the market and on Etsy.',
    bullets: [
      'Heat-resistant BOPP & matte stocks that won\'t curl on warm candle jars',
      'Oil-proof and waterproof — built for candles, soaps, sauces & cosmetics',
      'Gold & silver foil options for premium product lines',
      'Small-batch friendly quantities with instant quantity-break pricing',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    sections: [
      {
        heading: 'The right label material for candles and body products',
        body: 'Candle jars get warm, oily and handled constantly — paper labels curl, stain and peel. Our BOPP and matte polypropylene stocks resist heat and wick oils, staying flat and vivid for the life of the candle. For bath and body products, the same materials survive shower humidity and product spills without ink lift.',
      },
      {
        heading: 'From farmers market to retail shelf',
        body: 'Start with 250 labels for your first market weekend and scale to thousands as stockists pick you up — our live pricing shows every quantity break instantly so you can find your margin sweet spot. Add foil, embossing or clear "no-label-look" BOPP as your line goes premium, all from the same quote calculator.',
      },
    ],
    faqs: [
      {
        q: 'Will your labels curl or peel on warm candle jars?',
        a: 'No — we recommend BOPP or matte polypropylene stocks for candles. Both resist heat and wick oils, staying flat on the jar through burning cycles.',
      },
      {
        q: 'Are your product labels waterproof and oil-resistant?',
        a: 'Yes. BOPP materials are fully waterproof and oil-proof — ideal for candles, soaps, lotions, sauces and anything that lives in a bathroom or kitchen.',
      },
      {
        q: 'What label shape works best for candle tins and jars?',
        a: 'Round and oval labels suit lids and curved jars; rectangular wraps suit straight-sided vessels. We print any custom size and shape — enter your dimensions for an instant quote.',
      },
      {
        q: 'Do you have a minimum order for product labels?',
        a: 'Roll labels start at 250 pieces — sized for small-batch makers testing a new scent or product line.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Label Materials',
  },
  {
    slug: 'waterproof-labels',
    metaTitle: 'Waterproof Labels — Dishwasher, Freezer & Oil-Proof | Canada & USA',
    metaDescription:
      'Waterproof labels that survive dishwashers, freezers, showers & squeeze bottles. BOPP & vinyl stocks, any size & shape. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Waterproof Labels That Never Let Go',
    intro:
      'If your product touches water, ice, oil or steam, an ordinary paper label is a refund waiting to happen. Evergreen Graphics Group prints true waterproof labels on BOPP and vinyl stocks — the same materials used by national beverage and cosmetics brands — so your label looks perfect from the first fridge day to the last squeeze.',
    bullets: [
      'Fully waterproof BOPP & vinyl — not just "water-resistant" paper',
      'Freezer-safe, dishwasher-safe and oil-proof',
      'Clear, white, foil and writable waterproof stocks',
      'Rolls for applicators or short runs for hand application',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Waterproof vs water-resistant: know the difference',
        body: 'Many "water-resistant" labels are coated paper that survives a splash but wrinkles in condensation and delaminates in a cooler. True waterproof labels use a polypropylene (BOPP) or vinyl face with waterproof adhesive and inks — submerge them, freeze them, run them through a dishwasher and they stay flat, legible and stuck.',
      },
      {
        heading: 'Where waterproof labels earn their keep',
        body: 'Beverage bottles in ice buckets. Frozen food in retail freezers. Bath bombs and shampoo in the shower. Squeeze bottles of sauce on a restaurant table. Chemical and cleaning products in wet storage. If any of those sound like your product, waterproof BOPP is the default choice — and our live calculator prices your exact size instantly.',
      },
    ],
    faqs: [
      {
        q: 'Are your waterproof labels dishwasher safe?',
        a: 'Yes — BOPP and vinyl labels with our standard laminate survive repeated dishwasher cycles without peeling or ink loss.',
      },
      {
        q: 'Can waterproof labels go in the freezer?',
        a: 'Yes. Our BOPP stocks use freezer-grade adhesive that grips through frost, condensation and thaw cycles — ideal for frozen food packaging.',
      },
      {
        q: 'What is the best waterproof label material?',
        a: 'White BOPP is the all-round workhorse; clear BOPP gives a no-label look on glass; vinyl adds extra durability for chemical or outdoor use. All three are fully waterproof.',
      },
      {
        q: 'Can I write on a waterproof label?',
        a: 'Yes — our writable BOPP stock accepts pen and marker while staying waterproof, perfect for batch dates and best-before labelling.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Waterproof Materials',
  },
  {
    slug: 'custom-window-decals',
    metaTitle: 'Custom Window Decals & Storefront Graphics | Canada & USA',
    metaDescription:
      'Custom window decals, storefront graphics & static clings. Clear, opaque or removable — any size. Instant quotes — printed in Canada, shipped across Canada & the USA.',
    h1: 'Custom Window Decals That Turn Glass into Ad Space',
    intro:
      'Every storefront window is free advertising space — window decals put your hours, branding and promotions right where foot traffic looks. Evergreen Graphics Group prints clear decals, opaque vinyl and no-adhesive static clings for retail windows, office glass and vehicle windows, shipped anywhere in Canada and the USA.',
    bullets: [
      'Clear decals "float" your logo on glass — no white background box',
      'Static clings reposition endlessly — perfect for seasonal promos',
      'Inside-glass or outside-glass application options',
      'Removable adhesive comes off clean at lease end',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Pick the right window graphic',
        body: 'Clear vinyl decals disappear into the glass leaving just your design — ideal for logos and hours-of-operation. Opaque white vinyl blocks light for maximum colour punch on busy streets. Static clings need no adhesive at all, so seasonal sales and holiday art go up and come down in seconds, ready to reuse next year.',
      },
      {
        heading: 'Storefronts, offices and vehicle glass',
        body: 'Retailers rotate window promos monthly without residue. Offices add frosted-style privacy branding to meeting-room glass. Service businesses put booking URLs on vehicle rear windows. Tell us where the graphic is going and we\'ll match the material, adhesive and application side to the job.',
      },
    ],
    faqs: [
      {
        q: 'Do window decals go on the inside or outside of the glass?',
        a: 'Both are available. Inside-glass (reverse-printed) decals are protected from weather and vandalism; outside-glass decals offer maximum vibrancy. We\'ll print for whichever side you choose.',
      },
      {
        q: 'Will window decals leave residue when removed?',
        a: 'Our removable-adhesive decals come off clean without residue — and static clings use no adhesive at all, so glass stays spotless.',
      },
      {
        q: 'What is the difference between a window decal and a static cling?',
        a: 'Decals use adhesive and stay put for months or years. Static clings grip glass with static only, so they can be repositioned and reused — ideal for rotating seasonal promotions.',
      },
      {
        q: 'Can you print see-through window graphics?',
        a: 'Clear vinyl decals leave everything unprinted fully transparent, so your logo appears to float on the glass while visibility is maintained.',
      },
    ],
    categoryPath: '/category/decals-clings',
    categoryLabel: 'Browse Decals & Clings',
  },
  {
    slug: 'custom-car-decals',
    metaTitle: 'Custom Car Decals & Vehicle Magnets — Fleet Branding | Canada & USA',
    metaDescription:
      'Custom car decals, truck lettering & removable vehicle magnets. Vehicle-grade vinyl that survives winters & car washes. Instant quotes — ships across Canada & the USA.',
    h1: 'Custom Car Decals & Vehicle Magnets for Business',
    intro:
      'A branded vehicle generates thousands of impressions per day — the cheapest advertising a service business can buy. Evergreen Graphics Group prints vehicle-grade car decals and removable vehicle magnets for contractors, realtors, delivery fleets and food businesses across Canada and the USA.',
    bullets: [
      'Vehicle-grade vinyl rated for highway speeds, winters & car washes',
      'Removable vehicle magnets — brand the car on shift, blank off shift',
      'Any size: door logos, rear-window URLs, full lettering sets',
      'Fleet-friendly: reorder identical sets for every new vehicle',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Decals or magnets: which fits your business?',
        body: 'Permanent vinyl decals suit dedicated work vehicles — they survive years of highway driving, pressure washes and road salt. Vehicle magnets suit personal cars doing double duty: a 0.030" thick magnet grips steel doors at highway speed, then pulls off in seconds when the workday ends. Many businesses run both — decals on the fleet, magnets for staff cars.',
      },
      {
        heading: 'What converts on a moving vehicle',
        body: 'You have 3 seconds of attention at a stoplight. The formula that works: business name, one-line service description, and a big phone number or short URL — nothing else. Our team will flag artwork that\'s too dense to read at distance before it prints, so your rolling billboard actually rings the phone.',
      },
    ],
    faqs: [
      {
        q: 'Will car decals damage my vehicle\'s paint?',
        a: 'No — vehicle-grade vinyl is designed for automotive paint and removes clean with heat, even after years. Magnets are fully non-marking.',
      },
      {
        q: 'Do vehicle magnets stay on at highway speed?',
        a: 'Yes — our 0.030" thick vehicle magnets are rated for highway driving on flat steel surfaces. Remove them before automatic car washes and re-seat them clean and dry.',
      },
      {
        q: 'How long do car decals last?',
        a: 'Vehicle-grade vinyl with UV laminate lasts 3–5+ years outdoors through Canadian winters and US summers, resisting fading, salt and pressure washing.',
      },
      {
        q: 'Can you print matching sets for a whole fleet?',
        a: 'Yes — order identical decal or magnet sets in any quantity, and reorder the exact same spec when new vehicles join the fleet.',
      },
    ],
    categoryPath: '/category/unique-custom/product/vehicle-magnets',
    categoryLabel: 'Browse Vehicle Magnets',
  },
  {
    slug: 'die-cut-vs-kiss-cut-stickers',
    metaTitle: 'Die-Cut vs Kiss-Cut Stickers: Which Should You Order? | Evergreen Graphics Group',
    metaDescription:
      'Die-cut vs kiss-cut stickers explained: cutting style, peel experience, cost and best uses — with a side-by-side comparison table. Get an instant quote for either.',
    h1: 'Die-Cut vs Kiss-Cut Stickers: The Complete Comparison',
    intro:
      'Both die-cut and kiss-cut stickers are printed on the same waterproof vinyl with the same inks and laminate — the only difference is how deep the blade cuts. That one difference changes how the sticker looks in hand, how fast it peels, and which jobs each format wins. Here\'s the practical comparison.',
    bullets: [
      'Same vinyl, inks & laminate — only the cutting depth differs',
      'Die-cut: blade cuts through vinyl AND backing (shaped in hand)',
      'Kiss-cut: blade cuts vinyl only (square backing, easy peel)',
      'Both formats priced instantly in our quote calculator',
      'Printed in Canada · shipped across Canada & the USA',
    ],
    table: {
      caption: 'Die-cut vs kiss-cut at a glance',
      headers: ['', 'Die-Cut Stickers', 'Kiss-Cut Stickers'],
      rows: [
        ['Cut depth', 'Through vinyl + backing', 'Through vinyl only'],
        ['Shape in hand', 'Custom shape — premium feel', 'Square/rect backing around design'],
        ['Peel speed', 'Slower (find the edge)', 'Fast — backing tab to grab'],
        ['Intricate designs', 'Fragile edges can catch', 'Protected by surrounding backing'],
        ['Extra branding space', 'None', 'Printable backing border (logo, URL, QR)'],
        ['Best for', 'Retail merch, laptop & water bottle stickers', 'Giveaways, sticker sheets, planner packs'],
      ],
    },
    sections: [
      {
        heading: 'When die-cut wins',
        body: 'Die-cut is the premium retail format: the sticker IS the shape, which looks sharper on a merch table and photographs better for online stores. If the sticker is the product — band merch, artist drops, brand swag people pay for — die-cut\'s in-hand feel justifies itself.',
      },
      {
        heading: 'When kiss-cut wins',
        body: 'Kiss-cut is the workhorse for volume distribution: faster to peel at a trade-show booth, safer for intricate artwork, and the surrounding backing doubles as printable branding space. It\'s also the only format for multi-design sticker sheets, which cut per-design cost dramatically.',
      },
    ],
    faqs: [
      {
        q: 'Are die-cut and kiss-cut stickers the same price?',
        a: 'They price very similarly since material and printing are identical. Sticker sheets (kiss-cut) often work out cheaper per design because multiple designs share one sheet.',
      },
      {
        q: 'Which is more durable, die-cut or kiss-cut?',
        a: 'Once applied, they are identical — same vinyl, same laminate, same waterproof rating. Durability differences only exist before application (kiss-cut backing protects fragile edges in transit).',
      },
      {
        q: 'Can I get the same design in both formats?',
        a: 'Yes — upload once and order die-cut singles for retail plus kiss-cut sheets for giveaways. Our system generates the correct cutline for each format automatically.',
      },
    ],
    categoryPath: '/custom-die-cut-stickers',
    categoryLabel: 'Explore Die-Cut Stickers',
  },
  {
    slug: 'bopp-vs-vinyl-labels',
    metaTitle: 'BOPP vs Vinyl Labels: Which Material Is Right? | Evergreen Graphics Group',
    metaDescription:
      'BOPP vs vinyl labels compared: waterproofing, durability, cost and best uses for food, candles, cosmetics & industrial products — with a comparison table and instant quotes.',
    h1: 'BOPP vs Vinyl Labels: Choosing the Right Material',
    intro:
      'BOPP (biaxially oriented polypropylene) and vinyl are the two waterproof workhorses of product labelling — and picking the right one affects your cost, shelf look and label lifespan. The short version: BOPP for products, vinyl for punishment. Here\'s the full comparison.',
    bullets: [
      'Both are fully waterproof and oil-resistant — paper is neither',
      'BOPP: thinner, crisper, more economical — the retail-shelf standard',
      'Vinyl: thicker, tougher, more conformable — the outdoor/industrial pick',
      'Clear, white and metallic options available in both families',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    table: {
      caption: 'BOPP vs vinyl at a glance',
      headers: ['', 'BOPP Labels', 'Vinyl Labels'],
      rows: [
        ['Waterproof', 'Yes — fully', 'Yes — fully'],
        ['Typical lifespan', '2–3 years indoors', '3–5+ years incl. outdoors'],
        ['Thickness & feel', 'Thin, crisp, conforms to jars', 'Thicker, rugged, cushioned'],
        ['UV / weather', 'Good indoors, fair outdoors', 'Excellent — outdoor rated'],
        ['Chemical resistance', 'Good (oils, water)', 'Excellent (solvents, cleaners)'],
        ['Cost', 'More economical', 'Premium'],
        ['Best for', 'Food, beverage, candles, cosmetics', 'Chemical drums, outdoor gear, equipment'],
      ],
    },
    sections: [
      {
        heading: 'When BOPP is the right call',
        body: 'If your product sits on a shelf, in a fridge or in a bathroom — food, beverages, candles, soaps, cosmetics — BOPP is the industry default. It\'s waterproof, oil-proof, freezer-safe, conforms neatly to curved jars and squeeze bottles, and costs less than vinyl at every quantity. Clear BOPP also delivers the premium "no-label look" on glass.',
      },
      {
        heading: 'When to pay for vinyl',
        body: 'Vinyl earns its premium where labels take physical abuse: outdoor equipment, chemical containers, industrial drums, gear that gets dragged, hosed down or left in the sun. Its extra thickness resists scuffing and its adhesives grip textured and low-energy surfaces that BOPP struggles with.',
      },
    ],
    faqs: [
      {
        q: 'Is BOPP or vinyl better for candle labels?',
        a: 'BOPP. It resists heat and wick oils, conforms smoothly to curved jars and costs less — vinyl\'s outdoor toughness is wasted on a candle.',
      },
      {
        q: 'Which lasts longer outdoors, BOPP or vinyl?',
        a: 'Vinyl — with UV laminate it lasts 3–5+ years outside, versus fair outdoor performance for BOPP. For anything living outdoors, choose vinyl.',
      },
      {
        q: 'Are both materials food-safe?',
        a: 'Yes — our BOPP stocks are the standard for food and beverage labelling, and both materials use adhesives appropriate for exterior food packaging.',
      },
      {
        q: 'Can I get clear versions of both materials?',
        a: 'Yes — clear BOPP is the most popular "no-label look" option for glass; clear vinyl decals suit windows and equipment. Both price instantly in our calculator.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Label Materials',
  },
  {
    slug: 'custom-embroidered-patches',
    metaTitle: 'Custom Patches — Embroidered & Iron-On Patches | Canada & USA',
    metaDescription:
      'Custom embroidered and iron-on patches for uniforms, jackets, hats & merch. Any shape, low minimums. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Patches for Uniforms, Merch & Crews',
    intro:
      'From motorcycle clubs and sports teams to breweries and workwear, a custom patch turns any garment into branded gear. Evergreen Graphics Group produces embroidered-look emblems and iron-on patches with crisp detail and durable edges — shipped across Canada and the USA.',
    bullets: [
      'Iron-on or sew-on backing — your choice at checkout',
      'Any shape: circles, shields, rockers, full custom die-cut outlines',
      'Wash-durable threads and edges built for workwear & uniforms',
      'Low minimums for clubs, crews and small brands',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Patches that survive real wear',
        body: 'A patch fails at the edge first — fraying borders and lifting corners. Our emblems use sealed merrowed-style edges and high-density stitching detail, and iron-on backings are rated for repeated washing when applied at the right temperature. Full application instructions ship with every order.',
      },
      {
        heading: 'From one-off jackets to full uniform runs',
        body: 'Order a handful for a riding club or hundreds for a franchise uniform program — quantity-break pricing is calculated instantly. Upload your logo and our team flags any detail too fine to stitch cleanly before production, so what arrives matches what you approved.',
      },
    ],
    faqs: [
      {
        q: 'Are iron-on patches permanent?',
        a: 'Applied correctly with a household iron or heat press, iron-on patches survive regular machine washing. For heavy-duty workwear we recommend adding a few stitches or choosing sew-on backing for maximum lifespan.',
      },
      {
        q: 'What is the minimum order for custom patches?',
        a: 'We keep minimums low so clubs and small brands can order sensibly — enter your quantity in the quote calculator to see live pricing at every break.',
      },
      {
        q: 'Can you make custom-shaped patches?',
        a: 'Yes — circles, shields, rockers and fully custom die-cut outlines are all supported. Upload your artwork and we generate the outline for you.',
      },
      {
        q: 'Do you ship patches to the USA?',
        a: 'Yes — all patches ship across Canada and the United States with live courier rates shown at checkout.',
      },
    ],
    categoryPath: '/category/wearables/product/emblems-patches',
    categoryLabel: 'Browse Emblems & Patches',
  },
  {
    slug: 'custom-temporary-tattoos',
    metaTitle: 'Custom Temporary Tattoos — Events, Parties & Promos | Canada & USA',
    metaDescription:
      'Custom temporary tattoos for events, teams, birthdays & brand promos. Skin-safe, easy on/off, vivid full-colour. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Temporary Tattoos People Actually Wear',
    intro:
      'Nothing spreads a logo at an event like temporary tattoos — kids line up for them, fans wear them on camera, and every application is a walking endorsement. Evergreen Graphics Group prints skin-safe custom temporary tattoos in vivid full colour, from singles to booklet sets, shipped across Canada and the USA.',
    bullets: [
      'Skin-safe, FDA/Health-Canada compliant inks and adhesives',
      'Applies in seconds with water — removes with baby oil or rubbing alcohol',
      'Vivid full-colour printing including fine linework and metallic-look designs',
      'Singles for handouts or tattoo booklets for retail & party favours',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'The highest-engagement promo product per dollar',
        body: 'Temporary tattoos cost pennies apiece at volume but create photos, social posts and conversations no flyer ever will. Sports teams stamp fan sections, breweries brand festival crowds, and kids\' parties turn a logo into the day\'s activity. Every design is printed sharp enough for thin lines and small text.',
      },
      {
        heading: 'Singles, sheets and retail-ready booklets',
        body: 'Hand out die-cut singles at the gate, gang multiple designs on a sheet for parties, or order bound tattoo booklets that sell at retail and stuff loot bags. Our quote calculator prices every format and quantity instantly — no waiting on an email quote.',
      },
    ],
    faqs: [
      {
        q: 'Are your temporary tattoos safe for kids?',
        a: 'Yes — inks and adhesives are skin-safe and compliant with North American cosmetic safety standards. As with any skin product, avoid application on broken or sensitive skin.',
      },
      {
        q: 'How long do temporary tattoos last on skin?',
        a: 'Typically 2–5 days depending on placement and washing. They remove easily anytime with baby oil, rubbing alcohol or an adhesive-tape peel.',
      },
      {
        q: 'How do you apply a temporary tattoo?',
        a: 'Press the tattoo face-down on clean, dry skin, hold a wet cloth on the backing for 30 seconds, then peel — done. Instructions are included with every order.',
      },
      {
        q: 'Can I order multiple designs in one run?',
        a: 'Yes — combine designs on sheets or in booklets to cut per-design costs. Enter your sizes and quantities in the calculator for instant pricing.',
      },
    ],
    categoryPath: '/category/wearables/product/classic-tattoos',
    categoryLabel: 'Browse Temporary Tattoos',
  },
  {
    slug: 'sticker-printing-toronto',
    includeLocalBusiness: true,
    metaTitle: 'Sticker Printing Toronto & GTA — Local Custom Stickers | Evergreen Graphics Group',
    metaDescription:
      'Sticker printing in Toronto & the GTA — die-cut, holographic & waterproof stickers produced at our Scarborough facility. Instant online quotes, fast local turnaround.',
    h1: 'Sticker Printing in Toronto & the GTA',
    intro:
      'Need custom stickers printed in Toronto without the overseas wait? Evergreen Graphics Group produces die-cut, kiss-cut, holographic and waterproof stickers at our Scarborough facility — serving Toronto, Markham, North York, Etobicoke, Mississauga, Vaughan and the whole GTA with fast local turnaround and instant online quoting.',
    bullets: [
      'Produced locally at 90 Venture Dr #2, Scarborough — not drop-shipped overseas',
      'Fast GTA turnaround: local production means days, not weeks',
      'Die-cut, kiss-cut, holographic, glitter & waterproof vinyl',
      'Instant online pricing — no "request a quote" email tag',
      'Serving Toronto, Scarborough, Markham, Mississauga, Vaughan & beyond',
    ],
    sections: [
      {
        heading: 'A real Toronto print shop, with online-store speed',
        body: 'Most "Toronto" sticker sites are storefronts for printers thousands of kilometres away. Our production runs in Scarborough, which means tighter turnarounds for GTA businesses, real humans checking your artwork, and shipping that crosses the city — not the continent. Order online in minutes and your stickers are produced just up the 401.',
      },
      {
        heading: 'Who we print for across the GTA',
        body: 'Toronto breweries and cafés label cans and cups. Markham and Vaughan manufacturers mark products and packaging. Etsy sellers across Scarborough and North York ship branded parcels. Event teams downtown hand out die-cut logos by the thousand. Whatever the job, the quote calculator prices your exact size and quantity instantly.',
      },
    ],
    faqs: [
      {
        q: 'Where are your stickers actually printed?',
        a: 'At our own facility at 90 Venture Dr #2, Scarborough, ON — orders for Toronto and GTA customers are produced locally, not outsourced overseas.',
      },
      {
        q: 'How fast can Toronto customers get stickers?',
        a: 'Local production plus GTA courier distances means most orders arrive in days. Rush needs? Call us at (905) 447-0257 and we\'ll see what\'s possible.',
      },
      {
        q: 'Do you serve businesses outside Toronto?',
        a: 'Yes — while we\'re based in the GTA, we ship across all of Canada and the USA with live courier rates at checkout.',
      },
      {
        q: 'Can I get an instant price without emailing back and forth?',
        a: 'Yes — our online quote calculator prices any size, shape and quantity in seconds, with a live preview of your artwork and cutline.',
      },
    ],
    categoryPath: '/category/stickers',
    categoryLabel: 'Browse All Stickers',
  },
  {
    slug: 'label-printing-toronto',
    includeLocalBusiness: true,
    metaTitle: 'Label Printing Toronto & GTA — Custom Roll Labels | Evergreen Graphics Group',
    metaDescription:
      'Label printing in Toronto & the GTA — custom roll labels on BOPP, foil & waterproof stocks from our Scarborough facility. Instant quotes, fast local turnaround.',
    h1: 'Label Printing in Toronto & the GTA',
    intro:
      'Toronto\'s makers, food producers and manufacturers need labels fast — not in three weeks from overseas. Evergreen Graphics Group prints custom roll labels on BOPP, foil and waterproof stocks at our Scarborough facility, serving the entire GTA with local turnaround and instant online pricing.',
    bullets: [
      'Printed locally at 90 Venture Dr #2, Scarborough, ON',
      'Waterproof BOPP, clear no-label-look, gold & silver foil stocks',
      'Rolls ready for hand application or automatic applicators',
      'Perfect for GTA food, beverage, candle, cosmetic & industrial brands',
      'Instant online pricing — serving Toronto, Markham, Mississauga & beyond',
    ],
    sections: [
      {
        heading: 'Local labels for local products',
        body: 'From craft food producers in Leslieville to candle makers in Markham and manufacturers along the 407 corridor, GTA product businesses can\'t afford label delays. Local production means your rolls are made minutes away, questions get answered by a person in your time zone, and reorders land before you run out.',
      },
      {
        heading: 'Retail-ready materials, small-batch friendly',
        body: 'Start at 250 labels for a farmers-market weekend and scale to tens of thousands for distributor orders — every quantity break is priced live in the calculator. Waterproof BOPP for fridge and bathroom products, clear stock for the premium no-label look, and foil for lines that need shelf-pop.',
      },
    ],
    faqs: [
      {
        q: 'Do you print labels locally in Toronto?',
        a: 'Yes — production runs at our facility at 90 Venture Dr #2, Scarborough, ON, serving Toronto and the entire GTA with local turnaround.',
      },
      {
        q: 'What label materials do you stock?',
        a: 'White and clear BOPP (waterproof), gold and silver foil, semigloss paper and writable BOPP — all priced instantly in the online calculator.',
      },
      {
        q: 'What is your minimum label order?',
        a: 'Roll labels start at 250 pieces — sized for small-batch GTA makers testing a new product.',
      },
      {
        q: 'Can you match labels to my applicator machine?',
        a: 'Yes — tell us your unwind direction and core size and we\'ll supply rolls compatible with standard automatic and semi-automatic applicators.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Label Materials',
  },
  {
    slug: 'custom-thank-you-stickers',
    metaTitle: 'Custom Thank You Stickers & Packaging Stickers | Canada & USA',
    metaDescription:
      'Custom thank-you stickers and packaging stickers on rolls for small businesses. Seal parcels, brand unboxings, delight customers. Instant quotes — ships Canada & USA.',
    h1: 'Thank You & Packaging Stickers That Turn Parcels into Marketing',
    intro:
      'Every parcel you ship is a branding moment. A custom thank-you sticker seals the tissue paper, signs the box and turns an ordinary delivery into an unboxing worth photographing. Evergreen Graphics Group prints packaging stickers on convenient rolls — designed for Etsy shops, e-commerce brands and subscription boxes across Canada and the USA.',
    bullets: [
      'Supplied on rolls — fast to peel and apply on a packing bench',
      'Your logo, colours and message — not generic marketplace designs',
      'Matte, gloss, clear and kraft-look finishes to match your brand',
      'Repeat-friendly pricing: reorder the same spec in two clicks',
      'Instant online quotes — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Why branded beats generic',
        body: 'Generic gold "thank you" rolls from marketplaces tell customers you bought the same sticker as everyone else. A sticker with your logo, handle and brand colours costs pennies more per parcel and does real work: customers recognize the brand on arrival, tag you in unboxing posts, and remember who to reorder from. It\'s the cheapest impression you\'ll ever buy.',
      },
      {
        heading: 'One roll, many jobs',
        body: 'Seal tissue paper and mailer boxes, close poly bags, sign handwritten notes, brand coffee cups and takeout bags, or add a QR code that links to a review page or discount. Popular sizes run 1.5"–2" circles and squares — and our quote calculator prices any custom size and quantity instantly.',
      },
    ],
    faqs: [
      {
        q: 'What size should packaging stickers be?',
        a: 'The most popular sizes are 1.5" and 2" circles or squares — big enough to read, small enough to fit tissue seals, boxes and bags. Enter any custom size in the calculator for instant pricing.',
      },
      {
        q: 'Do you print custom thank-you stickers with my logo?',
        a: 'Yes — upload your logo or full design and we print exactly that. No templates, no generic designs, full colour included.',
      },
      {
        q: 'Are packaging stickers waterproof?',
        a: 'Choose our BOPP or vinyl stocks and they\'re fully waterproof and smudge-proof — parcels survive rain-soaked porches with the branding intact.',
      },
      {
        q: 'What quantity should a small shop start with?',
        a: 'Most small businesses start with 250–500 on a roll and step up as volume grows — quantity-break pricing is shown live so you can see exactly where unit costs drop.',
      },
    ],
    categoryPath: '/category/stickers',
    categoryLabel: 'Browse All Stickers',
  },
  {
    slug: 'bulk-stickers',
    metaTitle: 'Bulk Stickers & Wholesale Sticker Printing | Canada & USA',
    metaDescription:
      'Bulk custom stickers at wholesale-level pricing — 1,000 to 100,000+ units. Live quantity-break quotes, no sales calls. Printed in Canada, shipped across Canada & USA.',
    h1: 'Bulk Stickers at Prices That Scale With You',
    intro:
      'When you need thousands of stickers — for retail packs, product runs, events or resale — unit price is everything. Evergreen Graphics Group prints bulk custom stickers with transparent quantity-break pricing you can see instantly online: no "request a wholesale quote" forms, no sales calls, just the real number at 1,000, 10,000 or 100,000 units.',
    bullets: [
      'Live quantity-break pricing — watch the unit cost drop as you scale',
      'Consistent colour across runs: reorder and it matches',
      'Die-cut, kiss-cut, sheets or rolls at any volume',
      'Ideal for product packaging, events, franchises & resellers',
      'Printed in Canada — no overseas lead times or customs surprises',
    ],
    sections: [
      {
        heading: 'Wholesale pricing without the wholesale friction',
        body: 'Traditional wholesale printing hides pricing behind sales reps and minimum-order negotiations. Our calculator shows every quantity break up front, so agencies, resellers and brand managers can budget a 25,000-unit run at 11 pm without waiting for a callback. Need something beyond the calculator\'s ranges? One email gets a same-day custom volume quote.',
      },
      {
        heading: 'Volume without the overseas gamble',
        body: 'Offshore bulk printing looks cheap until a colour-shifted, mis-cut container arrives three weeks late with customs fees attached. Producing in Canada means proof approval before the full run, consistent colour batch to batch, and domestic shipping times to anywhere in Canada or the USA.',
      },
    ],
    faqs: [
      {
        q: 'How many stickers count as a bulk order?',
        a: 'Quantity breaks improve steeply from 1,000 units and keep dropping through 5,000, 10,000 and beyond. The calculator shows your exact unit price at every tier.',
      },
      {
        q: 'Do you offer reseller or agency pricing?',
        a: 'Our quantity-break pricing is already wholesale-competitive, and high-volume or recurring programs can contact us for dedicated terms.',
      },
      {
        q: 'Will colours match when I reorder?',
        a: 'Yes — we keep your artwork and production specs on file, so repeat runs match previous batches.',
      },
      {
        q: 'How fast can you turn around 10,000+ stickers?',
        a: 'Most bulk runs produce in days, not weeks, because printing happens at our own Canadian facility — no ocean freight in the timeline.',
      },
    ],
    categoryPath: '/category/stickers',
    categoryLabel: 'Browse All Stickers',
  },
  {
    slug: 'custom-logo-stickers',
    metaTitle: 'Custom Logo Stickers — Put Your Brand on Everything | Canada & USA',
    metaDescription:
      'Custom logo stickers die-cut to your exact logo shape. Waterproof vinyl, any size & quantity, instant online quotes. Printed in Canada — ships Canada & USA.',
    h1: 'Custom Logo Stickers, Cut to Your Exact Logo',
    intro:
      'Your logo already does the talking — a logo sticker just gives it more places to speak. Die-cut precisely to your logo\'s outline on waterproof vinyl, logo stickers brand laptops, packaging, equipment, vehicles, swag bags and storefronts. Upload once, and Evergreen Graphics Group generates the cutline automatically.',
    bullets: [
      'Die-cut to your logo\'s exact outline — automatic cutline generation',
      'Waterproof, UV-safe laminated vinyl that lasts years',
      'From 50 pieces for a launch to tens of thousands for distribution',
      'Matte, gloss, clear and holographic finishes',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'The lowest cost-per-impression in marketing',
        body: 'A logo sticker on a laptop travels to every coffee shop, meeting and airport its owner does — for years. Compared to any per-click ad, a sticker that costs under a dollar and earns thousands of views is the best media buy a brand can make. That\'s why swag tables, onboarding kits and every parcel out the door should carry one.',
      },
      {
        heading: 'Get the details right',
        body: 'Great logo stickers start with clean artwork: a high-resolution PNG with transparent background or a vector file (AI/SVG/PDF) gives the sharpest edges. Fine strokes thinner than 1mm can get fragile at small sizes — our preflight flags anything risky before production, and the live preview shows your exact cutline before you pay.',
      },
    ],
    faqs: [
      {
        q: 'What file do I need for logo stickers?',
        a: 'A transparent-background PNG or a vector file (AI, SVG, PDF) works best. Our system generates the die-cut line around your logo automatically.',
      },
      {
        q: 'What is the most popular logo sticker size?',
        a: '2"–3" across is the sweet spot for laptops and packaging. We print any size — enter dimensions in the calculator for live pricing.',
      },
      {
        q: 'Are logo stickers weatherproof?',
        a: 'Yes — laminated vinyl survives rain, sun, dishwashers and years of handling without fading or peeling.',
      },
      {
        q: 'Can I see my logo sticker before it prints?',
        a: 'Yes — the live preview shows your logo with its exact cutline, and AI lifestyle mockups show it on real-world products before you order.',
      },
    ],
    categoryPath: '/custom-die-cut-stickers',
    categoryLabel: 'Explore Die-Cut Stickers',
  },
  {
    slug: 'custom-wine-labels',
    metaTitle: 'Custom Wine, Beer & Beverage Labels | Canada & USA',
    metaDescription:
      'Custom wine labels, beer can labels & beverage labels. Ice-bucket-proof adhesives, premium foil & clear stocks. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Wine, Beer & Beverage Labels That Survive the Ice Bucket',
    intro:
      'A beverage label works in the toughest retail environment there is: fridges, coolers, condensation and ice buckets. Evergreen Graphics Group prints wine bottle labels, beer can labels and craft beverage labels on materials engineered for cold and wet — with the premium finishes that win the shelf grab.',
    bullets: [
      'Ice-bucket and fridge-rated adhesives — no lifting, no wrinkling',
      'Clear "no-label look", white BOPP, and gold & silver foil stocks',
      'Full wraps for cans, front-and-back sets for bottles',
      'Small-batch friendly: label a 250-bottle run or a full production line',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'Built for cold, wet and condensation',
        body: 'Paper labels wrinkle in a cooler and slide off in an ice bucket. Our BOPP stocks with cold-rated adhesives stay flat and bonded through refrigeration, submersion and the condensation cycle of a patio afternoon. For premium tiers, foil and specialty finishes like embossing add the shelf presence wine buyers expect.',
      },
      {
        heading: 'Craft producers, wineries and home bottlers',
        body: 'Ontario craft brewers wrap seasonal releases without committing to huge runs. Small wineries and cideries order front/back label sets by the hundred. Kombucha and cold-brew brands label bottles that live their whole life refrigerated. Even wedding and event bottlers order short custom runs — every format prices instantly in the calculator.',
      },
    ],
    faqs: [
      {
        q: 'Will your labels survive an ice bucket?',
        a: 'Yes — choose BOPP with our cold-rated adhesive and labels stay bonded and flat through submersion, refrigeration and condensation.',
      },
      {
        q: 'Can you print full-wrap beer can labels?',
        a: 'Yes — full wraps for standard 355ml and 473ml cans, plus custom dimensions for any container. Enter your size for an instant quote.',
      },
      {
        q: 'What is the minimum order for wine labels?',
        a: 'Roll labels start at 250 pieces — perfect for small-batch releases, private labels and event bottles.',
      },
      {
        q: 'Do you offer foil or embossed wine labels?',
        a: 'Yes — gold and silver foil stocks plus embossing and hot-stamping finishes are available for premium lines.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Label Materials',
  },
  {
    slug: 'custom-soap-labels',
    metaTitle: 'Custom Soap & Cosmetic Labels — Waterproof for Bath & Body | Canada & USA',
    metaDescription:
      'Custom soap labels, cosmetic labels & bath-product labels. Shower-proof, oil-resistant BOPP with premium finishes. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Soap & Cosmetic Labels That Survive the Shower',
    intro:
      'Bath and body products live in the wettest room of the house — and their labels have to look perfect anyway. Evergreen Graphics Group prints soap labels, cosmetic labels and skincare labels on shower-proof, oil-resistant stocks that keep small-batch products looking retail-grade from first use to last.',
    bullets: [
      'Waterproof, oil- and lotion-resistant BOPP stocks',
      'Squeezable material options that flex with tubes and soft bottles',
      'Clear no-label look, white, kraft-style and metallic foil finishes',
      'Compliant layout space for ingredients (INCI) and net-weight text',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'The right material for every bath product',
        body: 'Bar soap wraps and boxes suit premium matte stocks; anything bottled — shampoo, lotion, body oil, scrubs — needs waterproof BOPP that shrugs off shower spray and greasy fingerprints. Squeeze tubes need a flexible film that won\'t crack or wrinkle as the tube flexes. Tell us the container and we\'ll match the stock.',
      },
      {
        heading: 'Small-batch to boutique-retail ready',
        body: 'Start with 250 labels for a market table and scale to thousands as boutiques stock you. Fine print stays crisp for ingredient lists, and finishes like foil, embossing and clear-on-glass elevate a handmade product into the price tier it deserves. Pair with our candle labels for a full maker line.',
      },
    ],
    faqs: [
      {
        q: 'Are your soap and cosmetic labels waterproof?',
        a: 'Yes — BOPP stocks are fully waterproof and resistant to oils and lotions, built for bathroom and shower environments.',
      },
      {
        q: 'Will labels crack on squeeze tubes?',
        a: 'No — choose our flexible film option and the label flexes with the tube without cracking or wrinkling.',
      },
      {
        q: 'Can I fit ingredient lists in small print?',
        a: 'Yes — our print resolution keeps INCI ingredient lists and regulatory text legible even at small sizes; we flag anything too small before production.',
      },
      {
        q: 'What minimum order works for small makers?',
        a: 'Rolls start at 250 labels — sized for testing a new scent or product without over-committing.',
      },
    ],
    categoryPath: '/category/labels',
    categoryLabel: 'Browse Label Materials',
  },
  {
    slug: 'custom-magnets',
    metaTitle: 'Custom Magnets — Fridge, Promotional & Business Magnets | Canada & USA',
    metaDescription:
      'Custom fridge magnets, promotional magnets & magnetic business cards. Any shape, full colour, strong hold. Instant quotes — printed in Canada, ships Canada & USA.',
    h1: 'Custom Magnets That Keep Your Brand in the Kitchen',
    intro:
      'A flyer gets recycled; a magnet gets a spot on the fridge for years. Custom magnets keep your logo, phone number and booking link in the most-viewed spot in every home — which is why realtors, trades, restaurants and schools never stop ordering them. Evergreen Graphics Group prints full-colour custom magnets in any shape, shipped across Canada and the USA.',
    bullets: [
      'Full-colour printing on strong flexible magnet stock',
      'Any custom die-cut shape — houses, vans, badges, mascots',
      'Popular formats: business-card magnets, calendars, save-the-dates',
      'Strong hold that grips fridges, lockers, filing cabinets & toolboxes',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'The promo product people actually keep',
        body: 'Studies of promotional products consistently rank magnets among the longest-kept items — years of daily impressions from a one-time cost of well under a dollar. Realtors put their face on the fridge of every client, HVAC and plumbing companies keep their emergency number one glance away, and pizzerias make reordering effortless.',
      },
      {
        heading: 'Formats that work',
        body: 'Business-card magnets (3.5"x2") slot into every handshake and mailer. Die-cut shapes — a house for realtors, a van for movers, a slice for pizzerias — earn the fridge spot on charm alone. Save-the-date magnets outperform paper cards because guests keep the date literally in sight. Every format prices instantly in the calculator.',
      },
    ],
    faqs: [
      {
        q: 'What thickness are your custom magnets?',
        a: 'Standard promotional magnets use flexible 0.020"–0.030" stock — strong enough to hold notes on a fridge while staying economical at volume.',
      },
      {
        q: 'Can magnets be die-cut to a custom shape?',
        a: 'Yes — upload your design and we cut the magnet to its exact outline: logos, houses, vehicles, mascots, anything.',
      },
      {
        q: 'What size should a fridge magnet be?',
        a: 'Business-card size (3.5"x2") is the most popular; 2"–4" die-cut shapes also perform well. Any custom size prices instantly in the calculator.',
      },
      {
        q: 'Do you also make vehicle magnets?',
        a: 'Yes — thicker 0.030" vehicle-grade magnets for car doors are a separate product built for highway speeds. See our car decals & vehicle magnets page.',
      },
    ],
    categoryPath: '/category/unique-custom/product/magnets',
    categoryLabel: 'Browse Custom Magnets',
  },
  {
    slug: 'dtf-vs-screen-printing',
    metaTitle: 'DTF vs Screen Printing vs Vinyl (HTV): Which Is Best? | Evergreen Graphics Group',
    metaDescription:
      'DTF vs screen printing vs heat-transfer vinyl compared: cost per shirt, colour limits, durability, feel and minimums — with a side-by-side table. Instant DTF quotes.',
    h1: 'DTF vs Screen Printing vs Vinyl: The Honest Comparison',
    intro:
      'Every custom-apparel job comes down to three methods: DTF (direct-to-film) transfers, traditional screen printing, and heat-transfer vinyl (HTV). Each wins in a different scenario — and choosing wrong either wastes money on setup fees or hours on weeding vinyl. Here\'s the practical breakdown from a shop that presses all three.',
    bullets: [
      'DTF: full-colour, no minimums, no weeding — the modern default for 1–1,000 pieces',
      'Screen printing: cheapest per unit at high volume with few colours',
      'HTV vinyl: fine for single-colour one-offs, painful beyond a handful',
      'All three verdicts summarized in the table below',
      'Instant DTF transfer pricing in our online calculator',
    ],
    table: {
      caption: 'DTF vs screen printing vs HTV at a glance',
      headers: ['', 'DTF Transfers', 'Screen Printing', 'HTV Vinyl'],
      rows: [
        ['Colour limits', 'Unlimited, photo-quality', 'Priced per colour', 'One colour per layer'],
        ['Setup cost', 'None', 'Screens per colour/design', 'None, but manual weeding'],
        ['Best run size', '1–1,000+', '100+ (same design)', '1–20'],
        ['Fabrics', 'Cotton, poly, blends, denim', 'Mostly cotton/blends', 'Most fabrics'],
        ['Feel & stretch', 'Thin, stretches, no cracking', 'Softest at high mesh', 'Heavier, can crack'],
        ['Wash durability', '50+ washes', '50+ washes', '25–40 washes'],
        ['Gradients & photos', 'Yes', 'Difficult/costly', 'No'],
      ],
    },
    sections: [
      {
        heading: 'When DTF wins (most of the time)',
        body: 'DTF prints unlimited colour — gradients, photos, fine text — with zero setup fees and no weeding, presses in 15 seconds, and stretches without cracking for 50+ washes. For short and mid runs, multi-colour designs, mixed sizes or print-on-demand, DTF is now the industry default. Order by the piece or save with gang sheets.',
      },
      {
        heading: 'When screen printing or vinyl still make sense',
        body: 'Screen printing remains unbeatable on unit price for big single-design runs in one or two colours — think 500 identical event tees. HTV vinyl suits a single name-and-number jersey or a one-off gift when you already own a cutter. If your job doesn\'t look like those two cases, DTF will be faster, cheaper or better looking — usually all three.',
      },
    ],
    faqs: [
      {
        q: 'Is DTF cheaper than screen printing?',
        a: 'For runs under a few hundred pieces or any multi-colour design, yes — DTF has no per-colour screen fees. Screen printing only wins on very large single-design, few-colour runs.',
      },
      {
        q: 'Does DTF last as long as screen printing?',
        a: 'Properly pressed DTF survives 50+ washes without cracking — comparable to quality screen prints and better than HTV vinyl.',
      },
      {
        q: 'Can DTF print photos and gradients?',
        a: 'Yes — DTF is printed digitally, so photographic detail, gradients and fine linework come out exactly as designed. Screen printing and vinyl cannot match this economically.',
      },
      {
        q: 'Do I need special equipment to apply DTF transfers?',
        a: 'Just a heat press (or a household iron in a pinch): 300–325°F for 10–15 seconds. Full instructions ship with every order.',
      },
    ],
    categoryPath: '/custom-dtf-heat-transfers',
    categoryLabel: 'Explore DTF Transfers',
  },
  {
    slug: 'custom-clear-stickers',
    metaTitle: 'Custom Clear Stickers — Transparent, No-Background Look | Canada & USA',
    metaDescription:
      'Custom clear stickers printed on transparent vinyl — your design appears to float on glass, bottles & packaging. Waterproof, any shape. Instant quotes — ships Canada & USA.',
    h1: 'Custom Clear Stickers With the No-Background Look',
    intro:
      'Clear stickers print your design on transparent vinyl, so everything unprinted disappears — the logo appears to float directly on the bottle, window, laptop or packaging underneath. It\'s the cleanest, most premium sticker look there is, and Evergreen Graphics Group prints it waterproof in any shape and size.',
    bullets: [
      'Crystal-clear vinyl — no white box around your design',
      'Optional white-ink underlayer where you need colours to pop',
      'Waterproof and scratch-resistant laminate',
      'Perfect for glass, bottles, packaging and no-label-look branding',
      'Instant online pricing — printed in Canada, shipped Canada & USA',
    ],
    sections: [
      {
        heading: 'How clear stickers handle colour',
        body: 'Ink on transparent vinyl is naturally translucent — bold darks read beautifully, while light colours take on the surface behind them. Where you need full-strength colour, we print a white-ink underlayer behind selected areas so your design pops on any background. Our live preview shows exactly how your artwork will behave before you order.',
      },
      {
        heading: 'Where the no-label look wins',
        body: 'Beverage and cosmetic brands use clear stickers for glass jars and bottles where a white label would look pasted-on. Retailers brand windows and doors without blocking light. Creators layer clear stickers on laptops and gear for a decal-like finish. If the surface is part of the design, clear is the answer.',
      },
    ],
    faqs: [
      {
        q: 'Will white parts of my design show on a clear sticker?',
        a: 'Standard printing has no white ink — white areas stay transparent. Add the white-ink underlayer option and we print white exactly where your design needs it.',
      },
      {
        q: 'Are clear stickers waterproof?',
        a: 'Yes — clear vinyl with laminate is fully waterproof and scratch-resistant, safe for bottles, fridges and outdoor glass.',
      },
      {
        q: 'Do clear stickers work on dark surfaces?',
        a: 'Dark inks can disappear on dark surfaces unless you add the white underlayer, which keeps colours vivid on any background. The live preview shows the difference.',
      },
      {
        q: 'What is the difference between clear stickers and clear labels?',
        a: 'Same material family — "clear stickers" are usually die-cut singles while clear labels come on rolls for product packaging. We print both; pick the format in the calculator.',
      },
    ],
    categoryPath: '/category/stickers/product/transparent-stickers',
    categoryLabel: 'Browse Transparent Stickers',
  },
];
